import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Redirect } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Auth } from 'aws-amplify';
import UnathorizedLayout from '../../shared/layout/UnauthorizedLayout';
import { AlertContext } from '../../shared/app/AlertProvider';
import { IError, IStatus } from '../../../types';

const ActivateAccount = () => {
  const [validateTokenStatus, setValidateTokenStatus] = useState<IStatus>({
    value: 'idle',
    code: null,
  });
  const [username, setUsername] = useState('');
  const [redirect, setRedirect] = useState(false);
  const [isVerificationLinkExpired, setIsVerificationLinkExpired] = useState(
    false,
  );

  const { t } = useTranslation();

  const { showAlert } = useContext(AlertContext);

  const { search } = useLocation();

  useEffect(() => {
    setValidateTokenStatus({ value: 'pending', code: null });
    const username = new URLSearchParams(search).get('user_name') || '';
    const confirmationCode =
      new URLSearchParams(search).get('confirmation_code') || '';

    setUsername(username);
    Auth.confirmSignUp(username, confirmationCode)
      .then(() => {
        setValidateTokenStatus({ value: 'resolved', code: 200 });
        showAlert({
          severity: 'success',
          message: t('ActivateAccount.successful'),
        });
        setRedirect(true);
      })
      .catch((e: IError) => {
        if (
          // the account was already activated so no need to display an error message
          e.message === 'User cannot be confirmed. Current status is CONFIRMED'
        ) {
          showAlert({
            severity: 'success',
            message: t('ActivateAccount.successful'),
          });
          setRedirect(true);
        } else {
          showAlert({
            severity: 'error',
            message: t(
              'v2.global.snackbar_label_error_somethingWentWrong_short',
            ),
          });
          setIsVerificationLinkExpired(true);
          setValidateTokenStatus({ value: 'resolved', code: 200 });
        }
      });
  }, [search, showAlert, t]);

  if (redirect) {
    return <Redirect to="/login" />;
  }

  if (isVerificationLinkExpired) {
    return (
      <Redirect
        to={{
          pathname: '/registration/verification-expired',
          state: { username },
        }}
      />
    );
  }
  if (
    validateTokenStatus.value === 'idle' ||
    validateTokenStatus.value === 'pending'
  ) {
    return (
      <Box
        display="flex"
        flex={1}
        height="100%"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <UnathorizedLayout
      title={t('ActivateAccount.title')}
      subtitle={t('ActivateAccount.subtitle')}
    >
      Something went wrong
    </UnathorizedLayout>
  );
};

export default ActivateAccount;
